import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "@playbookai/api-clients";
import { kpiApi } from "../api/live-client.js";
import type {
  ManagedKpiObservationBatch,
  RecordKpiObservationsBatchInput,
} from "../api/types.js";
import { invalidateAfterObservationWrite } from "./kpiQueryKeys.js";

export interface RecordKpiObservationsVars {
  readonly kpiId: string;
  readonly input: RecordKpiObservationsBatchInput;
}

/**
 * Append SEVERAL readings to a KPI's ledger in one atomic request
 * (`POST /v1/kpis/:id/observations/batch`) — the historical backfill.
 *
 * ONE MUTATION, NOT A LOOP OF {@link useRecordKpiObservation}. That is the whole
 * reason this hook exists: the ledger is append-only, so a loop that failed
 * half-way would leave rows nobody can withdraw, and a client that then retried
 * would double the ones that succeeded. The server takes the array in a single
 * transaction, so this hook has exactly two outcomes to render.
 *
 * Staleifies through the shared {@link invalidateAfterObservationWrite} — the
 * same detail/observations/lists sweep the single write performs (a backfill
 * lands rows across every range), stated once so the two cannot drift.
 */
export function useRecordKpiObservations() {
  const config = useApiClient();
  const qc = useQueryClient();
  return useMutation<
    ManagedKpiObservationBatch,
    Error,
    RecordKpiObservationsVars
  >({
    mutationFn: ({ kpiId, input }) =>
      kpiApi.recordObservations(config, kpiId, input),
    onSuccess: (_data, { kpiId }) => invalidateAfterObservationWrite(qc, kpiId),
  });
}
